import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PaperSubmission.css';
import Navbar from './Navbar';
import Footer from './Footer';

const PaperSubmission = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasPayment, setHasPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    abstract: '',
    paperType: 'FULL PAPER',
    abstractCode: ''
  });
  
  const [paperFile, setPaperFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [userAbstracts, setUserAbstracts] = useState([]);
  const [existingPapers, setExistingPapers] = useState([]);
  const [showExistingPaperDialog, setShowExistingPaperDialog] = useState(false);
  const [selectedExistingPaper, setSelectedExistingPaper] = useState(null);
  const [abstractsLoading, setAbstractsLoading] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    const uid = sessionStorage.getItem('uid');
    
    console.log('🔐 Auth check - Token:', token ? 'exists' : 'missing');
    console.log('🔐 Auth check - UID:', uid ? 'exists' : 'missing');

    if (token && uid) {
      console.log('🔐 User authenticated');
      setIsAuthenticated(true);
      fetchUserData(uid, token);
    } else {
      console.log('🔐 User not authenticated, redirecting...');
      setErrorMessage('Please log in to submit a paper.');
      setTimeout(() => {
        navigate('/login-signup?redirect=/stis2025/paper-submission');
      }, 2000);
    }
  }, [navigate]);

  const fetchUserData = async (uid, token) => {
    try {
      console.log('🔍 Starting to fetch user data for UID:', uid);
      
      // Fetch user info to check payment status
      console.log('📞 Calling user-info API...');
      const userResponse = await axios.get(`https://api.rescons.in/user-info/${uid}`);
      console.log('✅ User info response:', userResponse.data);
      
      if (userResponse.data && userResponse.data.payments && userResponse.data.payments.length > 0) {
        setHasPayment(true);
        console.log('💰 Payment found, setting hasPayment to true');
      } else {
        console.log('❌ No payment found');
      }
      
      // Fetch user's abstracts
      console.log('📞 Calling abstracts API...');
      const abstractsResponse = await axios.get(`https://api.rescons.in/get-abstracts-by-user/${uid}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ Abstracts response:', abstractsResponse.data);
      
      // Fetch user's existing papers
      console.log('📞 Calling papers API...');
      const papersResponse = await axios.get(`https://api.rescons.in/api/papers/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ Papers response:', papersResponse.data);
      setExistingPapers(papersResponse.data.papers || []);
      
      if (abstractsResponse.data && abstractsResponse.data.abstracts) {
        console.log('📋 All abstracts:', abstractsResponse.data.abstracts);
        
        // Filter only approved abstracts
        const approvedAbstracts = abstractsResponse.data.abstracts.filter(abstract => {
          console.log('🔍 Checking abstract:', abstract.abstractCode, 'Status:', abstract.status, 'Submissions:', abstract.abstractSubmissions);
          
          // Check if abstractSubmissions exists and has approved status
          if (abstract.abstractSubmissions && abstract.abstractSubmissions.length > 0) {
            const hasApproved = abstract.abstractSubmissions.some(submission => {
              console.log('📝 Submission status:', submission.status);
              return submission.status === 'Approved';
            });
            console.log('✅ Has approved submission:', hasApproved);
            return hasApproved;
          }
          // Fallback: check direct status field
          const directStatus = abstract.status === 'Approved' || abstract.status === 'Accepted';
          console.log('📋 Direct status check:', directStatus);
          return directStatus;
        });
        
        console.log('🎯 Approved abstracts:', approvedAbstracts);
        setUserAbstracts(approvedAbstracts);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      // Set error message for user
      if (error.response?.status === 401) {
        setErrorMessage('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        setErrorMessage('User data not found. Please check your account.');
      } else {
        setErrorMessage('Failed to load user data. Please try again later.');
      }
    } finally {
      setCheckingPayment(false);
      setAbstractsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAbstractSelect = (abstractCode) => {
    const selectedAbstract = userAbstracts.find(abs => abs.abstractCode === abstractCode);
    if (selectedAbstract) {
      setFormData(prev => ({
        ...prev,
        abstractCode: abstractCode,
        title: selectedAbstract.title || '',
        abstract: selectedAbstract.mainBody || ''
      }));
    }
  };

  const handleAbstractCodeChange = (e) => {
    const selectedCode = e.target.value;
    if (selectedCode) {
      // Check if paper already exists for this abstract
      const existingPaper = existingPapers.find(paper => paper.abstractCode === selectedCode);
      if (existingPaper) {
        setSelectedExistingPaper(existingPaper);
        setShowExistingPaperDialog(true);
        // Reset form data
        setFormData(prev => ({
          ...prev,
          abstractCode: '',
          title: '',
          abstract: ''
        }));
        return;
      }
      handleAbstractSelect(selectedCode);
    } else {
      setFormData(prev => ({
        ...prev,
        abstractCode: '',
        title: '',
        abstract: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type - only .doc and .docx allowed
      const allowedTypes = ['.doc', '.docx'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExtension)) {
        setFileError('Please select a .doc or .docx file only. PDF files are not allowed.');
        setPaperFile(null);
        return;
      }

      // Validate file size (20MB)
      if (file.size > 10 * 1024 * 1024) {
        setFileError('File size must be less than 10MB');
        setPaperFile(null);
        return;
      }

      setFileError('');
      setPaperFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {

      // Validate required fields
      const requiredFields = ['title', 'abstract', 'paperType', 'abstractCode'];

      for (const field of requiredFields) {
        if (!formData[field].trim()) {
          throw new Error(`Please fill in all required fields. Missing: ${field}`);
        }
      }

      if (!paperFile) {
        throw new Error('Please select a paper file');
      }

      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitData.append(key, formData[key]);
      });

      // Add file
      submitData.append('paperFile', paperFile);

      const token = sessionStorage.getItem('token');
      console.log('🔐 Token from sessionStorage:', token ? 'Token exists' : 'No token');
      console.log('🔐 Token value:', token);
      
      const response = await axios.post(
        'https://api.rescons.in/api/papers/submit',
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccessMessage('Paper submitted successfully!');
      
      // Reset form
      setFormData({
        title: '',
        abstract: '',
        paperType: 'FULL PAPER',
        abstractCode: ''
      });
      setPaperFile(null);
      
             // Redirect to paper status page after 2 seconds
       setTimeout(() => {
         navigate(`/paper-submission-status?code=${response.data.paper.paperCode}`);
       }, 2000);

         } catch (error) {
       console.error('Paper submission error:', error);
       
       // Prepare error message for modal
       let errorMsg = '';
       if (error.response?.status === 409) {
         errorMsg = 'A paper has already been submitted for this abstract. Only one paper submission is allowed per abstract.';
       } else if (error.response?.data?.error) {
         errorMsg = error.response.data.error;
       } else if (error.message) {
         errorMsg = error.message;
       } else {
         errorMsg = 'Failed to submit paper. Please try again.';
       }
       
       // Show error in modal
       setModalError(errorMsg);
       setShowErrorModal(true);
       
       // Also set inline error message
       setErrorMessage(errorMsg);
     } finally {
       setLoading(false);
     }
  };

  if (!isAuthenticated) {
    return (
      <>
        <Navbar />
        <div className="paper-submission">
          <div className="paper-submission-wrapper">
            <div className="loading-message">
              <p>{errorMessage || 'Loading...'}</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="paper-submission">
        <div className="paper-submission-wrapper">
          {/* Header */}
          <header className="paper-header">
            <h1>Paper Submission</h1>
            <p className="paper-description">
              Submit your full paper for STIS-V 2025. Please ensure you follow the paper template and guidelines.
            </p>
          </header>

          {/* Success/Error Messages */}
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}
          
          {errorMessage && (
            <div className="error-message">
              {errorMessage}
            </div>
          )}

          <div className="paper-submission-content">
              {/* Left Column */}
              <div className="paper-left-column">
                {/* Abstract Selection */}
                <div className="form-section">
                  <h3>Paper Submission</h3>
                  <p className="section-description">
                    You are submitting a paper for your accepted abstract.
                  </p>
                  {abstractsLoading ? (
                    <div className="loading-abstracts">
                      <div className="loading-spinner"></div>
                      <p>Loading your accepted abstract...</p>
                    </div>
                  ) : userAbstracts.length === 1 ? (
                    <div className="abstract-selection-container">
                      <div className="selected-abstract-info">
                        <div className="abstract-status-badge approved">
                          ✓ Accepted Abstract
                        </div>
                        <p className="abstract-note">
                          You are submitting paper for abstract code: <strong>{userAbstracts[0].abstractCode}</strong>
                        </p>
                        <p><strong>Title:</strong> {userAbstracts[0].title}</p>
                        {/* <p><strong>Abstract:</strong> {userAbstracts[0].mainBody}</p> */}
                      </div>
                    </div>
                  ) : (
                    <div className="no-abstracts-message">
                      <div className="warning-icon">⚠️</div>
                      <h4>No Accepted Abstracts Found</h4>
                      <p>You don't have any accepted abstracts yet. Only accepted abstracts are eligible for paper submission.</p>
                      <div className="action-buttons">
                        <button 
                          className="submit-abstract-btn"
                          onClick={() => navigate('/')}
                        >
                          Back To Home
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Template Download */}
                <div className="form-section">
                  <h3>Paper Template</h3>
                  <p className="section-description">
                    Download the official paper template to ensure your submission meets the required format.
                  </p>
                  
                  <a
                    href="/stis2025/assets/Paper-Template.docx"
                    className="template-download-btn"
                    download
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                    </svg>
                    Download Paper Template
                  </a>
                </div>
              </div>

              {/* Right Column */}
              <div className="paper-right-column">
                {/* Submission Form */}
                {userAbstracts.length === 1 && (() => {
                  const approvedAbstract = userAbstracts[0];
                  const existingPaper = existingPapers.find(paper => paper.abstractCode === approvedAbstract.abstractCode);
                  if (existingPaper) {
                    return (
                      <div className="freeze-message">
                        <h3>Paper Already Submitted</h3>
                        <p>You have already submitted a paper for abstract code: <strong>{approvedAbstract.abstractCode}</strong>.</p>
                        {/* <button className="submit-btn" disabled>Freeze</button> */}
                      </div>
                    );
                  }
                  // Pre-fill formData if not already set
                  if (formData.abstractCode !== approvedAbstract.abstractCode) {
                    setFormData(prev => ({
                      ...prev,
                      abstractCode: approvedAbstract.abstractCode,
                      title: approvedAbstract.title || '',
                      abstract: approvedAbstract.mainBody || ''
                    }));
                  }
                  return (
                    <form onSubmit={handleSubmit} className="paper-submission-form">
                      <div className="form-section">
                        <h3>Paper Information</h3>
                        
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="title">Paper Title *</label>
                            <input
                              type="text"
                              id="title"
                              name="title"
                              value={formData.title}
                              onChange={handleInputChange}
                              required
                              placeholder="Enter paper title"
                              readOnly
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="paperType">Paper Type *</label>
                            <select
                              id="paperType"
                              name="paperType"
                              value={formData.paperType}
                              onChange={handleInputChange}
                              required
                            >
                              <option value="FULL PAPER">FULL PAPER</option>
                              <option value="EXTENDED ABSTRACT">EXTENDED ABSTRACT</option>
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="abstract">Abstract *</label>
                          <textarea
                            id="abstract"
                            name="abstract"
                            value={formData.abstract}
                            onChange={handleInputChange}
                            required
                            placeholder="Enter paper abstract"
                            rows="4"
                            readOnly
                          />
                        </div>
                      </div>

                      <div className="form-section">
                        <h3>Paper File Upload</h3>
                        <p className="section-description">
                          Upload your paper file. Supported formats: .doc, .docx only (Max size: 10MB)
                        </p>
                        
                        <div className="form-group">
                          <label htmlFor="paperFile">Paper File *</label>
                          <input
                            type="file"
                            id="paperFile"
                            onChange={handleFileChange}
                            accept=".doc,.docx"
                            required
                            // disabled
                          />
                          {fileError && <div className="error-text">{fileError}</div>}
                        </div>
                      </div>

                      <div className="form-actions">
                        <button
                          type="submit"
                          className="submit-btn"
                          // disabled={loading}
                          disabled
                          
                        >
                          {loading ? 'Submitting...' : 'Submit Paper'}
                        </button>
                      </div>
                    </form>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      
      {/* Existing Paper Dialog */}
      {showExistingPaperDialog && selectedExistingPaper && (
        <div className="existing-paper-dialog-overlay">
          <div className="existing-paper-dialog">
            <div className="dialog-header">
              <h3>⚠️ Paper Already Submitted</h3>
              <button 
                className="close-dialog-btn"
                onClick={() => setShowExistingPaperDialog(false)}
              >
                ×
              </button>
            </div>
            
            <div className="dialog-content">
              <div className="warning-message">
                <p>A paper has already been submitted for this abstract.</p>
              </div>
              
              <div className="existing-paper-details">
                <h4>Existing Paper Details:</h4>
                <div className="paper-info-grid">
                  <div className="info-item">
                    <label>Paper Code:</label>
                    <span>{selectedExistingPaper.paperCode}</span>
                  </div>
                  <div className="info-item">
                    <label>Title:</label>
                    <span>{selectedExistingPaper.title}</span>
                  </div>
                  <div className="info-item">
                    <label>Paper Type:</label>
                    <span>{selectedExistingPaper.paperType}</span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className={`status-badge ${selectedExistingPaper.status.toLowerCase().replace(' ', '-')}`}>
                      {selectedExistingPaper.status}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Submitted:</label>
                    <span>{new Date(selectedExistingPaper.submittedAt).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
              
              <div className="dialog-actions">
                                 <button 
                   className="view-paper-btn"
                   onClick={() => {
                     setShowExistingPaperDialog(false);
                     navigate(`/paper-submission-status?code=${selectedExistingPaper.paperCode}`);
                   }}
                 >
                  View Paper Status
                </button>
                <button 
                  className="close-btn"
                  onClick={() => setShowExistingPaperDialog(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="modal-overlay">
          <div className="modal-content error-modal">
            <div className="modal-header">
              <h3>❌ Paper Submission Error</h3>
              <button 
                className="close-modal-btn"
                onClick={() => {
                  setShowErrorModal(false);
                  setModalError('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="error-content">
                <div className="error-icon">⚠️</div>
                <p className="error-message">{modalError}</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="close-btn"
                onClick={() => {
                  setShowErrorModal(false);
                  setModalError('');
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </>
  );
};

export default PaperSubmission; 